import React, { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import { useSoftUIController, setMiniSidenav } from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const [openMenus, setOpenMenus] = useState({});

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const handleToggleMenu = (key) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isChildActive = (children) => {
    return children?.some((child) => child.key === collapseName);
  };

  const renderRoutes = routes.map(({ type, name, icon, children, key, route, href }) => {
    let returnValue;

    if (type === "collapse") {
      if (children && children.length > 0) {
        const isOpen = openMenus[key] || isChildActive(children);
        const parentActive = isChildActive(children);

        returnValue = (
          <React.Fragment key={key}>
            <SidenavCollapse
              color={color}
              name={name}
              icon={icon}
              active={false}
              parentActive={parentActive}
              expanded={isOpen}
              onClick={() => handleToggleMenu(key)}
              hasChildren
            />
            {isOpen && (
              <List sx={{ py: 0.25, pl: 0 }}>
                {children.map((child) => (
                  <NavLink to={child.route} key={child.key} style={{ textDecoration: "none" }}>
                    <SidenavCollapse
                      color={color}
                      name={child.name}
                      icon={<Icon sx={{ fontSize: "0.45rem" }}>fiber_manual_record</Icon>}
                      active={child.key === collapseName}
                      isChild
                    />
                  </NavLink>
                ))}
              </List>
            )}
          </React.Fragment>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              color={color}
              name={name}
              icon={icon}
              active={key === collapseName}
            />
          </Link>
        ) : (
          <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              color={color}
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
            />
          </NavLink>
        );
      }
    }

    return returnValue;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox
        px={2.5}
        pt={2.5}
        pb={1.5}
        textAlign="center"
        sx={{
          background: "linear-gradient(135deg, #0284C7 0%, #0EA5E9 100%)",
          borderRadius: "16px",
          mx: 1.2,
          mt: 1.2,
          boxShadow: "0 10px 26px rgba(2,132,199,.22)",
        }}
      >
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.5}
          onClick={closeSidenav}
          sx={{ cursor: "pointer", color: "#fff" }}
        >
          <Icon sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>close</Icon>
        </SoftBox>
        <SoftBox component={NavLink} to="/dashboard" display="flex" alignItems="center" justifyContent="center">
          <SoftBox sx={{ opacity: miniSidenav ? 0 : 1, transition: "opacity 0.3s ease" }}>
            <SoftTypography
              component="h6"
              variant="button"
              fontWeight="bold"
              sx={{ color: "#fff", fontSize: "1rem", letterSpacing: "0.02em" }}
            >
              {brandName}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      <Divider sx={{ mx: 2, my: 1.5, borderColor: "#E2E8F0" }} />

      <List sx={{ pt: 0.5, pb: 2, px: 0.5 }}>{renderRoutes}</List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
